/**
 * AI audience-research modal: niches, channels and copy angles.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function AudienceModal({ product, onClose }) {
  const [data, setData] = useState(product?.audience_research || null);
  const [loading, setLoading] = useState(false);
  const [extra, setExtra] = useState("");

  const run = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const { data: out } = await api.post(
        `/products/${product.id}/audience-research`,
        { extra_context: extra, force_refresh: forceRefresh },
      );
      setData(out);
      toast.success(out.source === "cached" ? "Mostrando resultado en caché" : "Investigación lista");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo investigar");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="audience-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={20} weight="duotone" className="text-[#7C3AED]" />
              <h3 className="font-bold text-lg">Dónde publicar este producto</h3>
            </div>
            <p className="text-xs text-zinc-600">
              <span className="font-semibold">{product.nombre}</span> — la IA propone tipos de grupos, hashtags
              y búsquedas para que tú encuentres y publiques manualmente.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-ink text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="mb-4 flex gap-2 items-end">
          <label className="flex-1">
            <span className="text-[10px] uppercase tracking-wide font-bold text-zinc-500">Contexto adicional (opcional)</span>
            <input
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Ej. Vendo en CDMX zona Roma, prefiero entrega en mano"
              className="mt-0.5 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
              data-testid="audience-extra"
            />
          </label>
          <button
            type="button"
            onClick={() => run(!!data)}
            disabled={loading}
            className="px-4 py-2 text-sm font-bold bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-50"
            data-testid="audience-run"
          >
            <Sparkle size={11} weight="fill" className="inline mr-1" />
            {loading ? "Investigando…" : data ? "Volver a investigar" : "Investigar con IA"}
          </button>
        </div>

        {data && (
          <div data-testid="audience-results">
            {data.tono_recomendado && (
              <div className="mb-4 px-3 py-2 bg-violet-50 border-l-4 border-violet-400">
                <div className="text-[10px] uppercase tracking-wide font-bold text-violet-700">Tono recomendado</div>
                <p className="text-sm text-zinc-800 mt-0.5">{data.tono_recomendado}</p>
                {data.horarios_pico && (
                  <p className="text-xs text-zinc-600 mt-1">⏰ Horarios pico: {data.horarios_pico}</p>
                )}
              </div>
            )}

            <AudienceSection
              title="Buyer personas"
              icon="🎯"
              items={data.buyer_personas}
              render={(p) => <span>{p}</span>}
            />

            <AudienceSection
              title="Subreddits"
              icon="🤖"
              items={data.subreddits}
              render={(s) => (
                <div className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold font-mono">{s.nombre}</div>
                    <div className="text-zinc-600">{s.porque}</div>
                  </div>
                  <button onClick={() => copy(s.nombre)} className="shrink-0 text-zinc-500 hover:text-klein" title="Copiar">
                    <CopyIcon />
                  </button>
                </div>
              )}
            />

            <AudienceSection
              title="Grupos de Facebook (búscalos manualmente)"
              icon="📘"
              items={data.facebook_groups}
              render={(g) => (
                <div>
                  <div className="font-semibold">{g.tipo}</div>
                  <div className="text-zinc-600">{g.porque}</div>
                  {g.buscar_en_fb && (
                    <div className="mt-1 flex items-center gap-2">
                      <code className="bg-white border border-zinc-200 px-2 py-0.5 text-[11px]">{g.buscar_en_fb}</code>
                      <button onClick={() => copy(g.buscar_en_fb)} className="text-zinc-500 hover:text-klein" title="Copiar">
                        <CopyIcon />
                      </button>
                      <a
                        href={`https://www.facebook.com/search/groups/?q=${encodeURIComponent(g.buscar_en_fb)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-klein hover:underline text-[11px]"
                      >
                        Buscar en FB →
                      </a>
                    </div>
                  )}
                </div>
              )}
            />

            <AudienceSection
              title="Canales de Telegram"
              icon="✈️"
              items={data.telegram_channels}
              render={(t) => (
                <div>
                  <div className="font-semibold">{t.tipo}</div>
                  <div className="text-zinc-600">{t.porque}</div>
                  {t.buscar_en_telegram && (
                    <div className="mt-1 flex items-center gap-2">
                      <code className="bg-white border border-zinc-200 px-2 py-0.5 text-[11px]">{t.buscar_en_telegram}</code>
                      <button onClick={() => copy(t.buscar_en_telegram)} className="text-zinc-500 hover:text-klein"><CopyIcon /></button>
                    </div>
                  )}
                </div>
              )}
            />

            <AudienceSection
              title="Grupos de WhatsApp (pídelos al admin)"
              icon="💬"
              items={data.whatsapp_groups}
              render={(w) => (
                <div>
                  <div className="font-semibold">{w.tipo}</div>
                  <div className="text-zinc-600">{w.porque}</div>
                  {w.donde_pedir_invitacion && (
                    <div className="text-[11px] text-zinc-500 mt-1">📍 {w.donde_pedir_invitacion}</div>
                  )}
                </div>
              )}
            />

            <AudienceSection
              title="Servidores de Discord"
              icon="🎮"
              items={data.discord_servers}
              render={(d) => (
                <div>
                  <div className="font-semibold">{d.tipo}</div>
                  <div className="text-zinc-600">{d.porque}</div>
                  {d.buscar_en && <div className="text-[11px] text-zinc-500 mt-1">🔎 {d.buscar_en}</div>}
                </div>
              )}
            />

            <AudienceSection
              title="Hashtags Instagram"
              icon="📷"
              items={data.instagram_hashtags}
              render={(h) => (
                <div className="flex justify-between gap-2">
                  <span className="font-mono">#{String(h).replace(/^#/, "")}</span>
                  <button onClick={() => copy(`#${String(h).replace(/^#/, "")}`)} className="text-zinc-500 hover:text-klein"><CopyIcon /></button>
                </div>
              )}
            />

            <AudienceSection
              title="Hashtags TikTok"
              icon="🎵"
              items={data.tiktok_hashtags}
              render={(h) => (
                <div className="flex justify-between gap-2">
                  <span className="font-mono">#{String(h).replace(/^#/, "")}</span>
                  <button onClick={() => copy(`#${String(h).replace(/^#/, "")}`)} className="text-zinc-500 hover:text-klein"><CopyIcon /></button>
                </div>
              )}
            />

            <AudienceSection
              title="Foros / sitios web"
              icon="🌐"
              items={data.forums}
              render={(f) => (
                <div>
                  <div className="font-semibold">{f.nombre}</div>
                  <div className="text-zinc-600">{f.porque}</div>
                  {f.url && (
                    <a href={f.url.startsWith("http") ? f.url : `https://${f.url}`} target="_blank" rel="noopener noreferrer" className="text-klein hover:underline text-[11px]">
                      {f.url} →
                    </a>
                  )}
                </div>
              )}
            />

            <AudienceSection
              title="Búsquedas a probar en Google"
              icon="🔍"
              items={data.search_queries}
              render={(q) => (
                <div className="flex justify-between gap-2">
                  <code className="bg-white border border-zinc-200 px-2 py-0.5 text-[11px]">{q}</code>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => copy(q)} className="text-zinc-500 hover:text-klein"><CopyIcon /></button>
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(q)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-klein hover:underline text-[11px] whitespace-nowrap"
                    >
                      Buscar →
                    </a>
                  </div>
                </div>
              )}
            />

            <AudienceSection
              title="Influencers / colaboradores"
              icon="⭐"
              items={data.influencer_types}
              render={(i) => <span>{i}</span>}
            />

            <AudienceSection
              title="Marketplaces a considerar"
              icon="🛒"
              items={data.marketplaces}
              render={(m) => <span>{m}</span>}
            />

            <div className="mt-5 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <strong>Recordatorio:</strong> Publica manualmente respetando las reglas de cada grupo.
              Una vez que encuentres un grupo que te funcione, agrégalo en <a href="/canales" className="text-klein hover:underline font-bold">Canales</a> para medir su rendimiento.
            </div>
          </div>
        )}
        {!data && !loading && (
          <div className="text-center py-12 text-sm text-zinc-500">
            Pulsa «Investigar con IA» para que el modelo proponga dónde publicar este producto.
          </div>
        )}
      </div>
    </div>
  );
}


function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// Renders one labelled section of the AudienceModal. Extracted from the parent
// so React doesn't see a fresh component type on every render.


function AudienceSection({ title, icon, items, render }) {
  const list = items || [];
  if (!list.length) return null;
  return (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-wide font-bold text-zinc-500 mb-1.5 flex items-center gap-1.5">
        {icon} {title} <span className="text-zinc-400 font-normal">· {list.length}</span>
      </div>
      <div className="space-y-1.5">
        {list.map((it, i) => (
          <div key={`${title}-${i}`} className="text-xs bg-zinc-50 border border-zinc-200 px-3 py-2">
            {render(it)}
          </div>
        ))}
      </div>
    </div>
  );
}
